import { useCallback, useState } from "react";

import type { IGroupBy } from "models";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { ScenarioField } from "../types/field";

export interface UseFieldSelectionProps {
  selectedFields: ScenarioField[];
  onAddField: (field: ScenarioField) => void;
  onRemoveField: (fieldId: string) => void;
}

export interface UseFieldSelectionReturn {
  isFieldSelectorOpen: boolean;
  openFieldSelector: () => void;
  closeFieldSelector: () => void;
  handleFieldSelect: (
    value: IGroupBy,
    forecastToggleOn: boolean,
    selectedForecastFolder: string,
    selectedForecastFolderName: string,
    selectedPdenSource: PdenSourceEnum
  ) => void;
  handleRemoveField: (fieldId: string) => void;
}

/**
 * Hook for managing field selection state and behavior
 * Separates business logic from presentation for easier testing
 */
export const useFieldSelection = ({
  selectedFields,
  onAddField,
  onRemoveField
}: UseFieldSelectionProps): UseFieldSelectionReturn => {
  const [isFieldSelectorOpen, setIsFieldSelectorOpen] = useState(false);

  const openFieldSelector = useCallback(() => {
    setIsFieldSelectorOpen(true);
  }, []);

  const closeFieldSelector = useCallback(() => {
    setIsFieldSelectorOpen(false);
  }, []);

  const handleFieldSelect = useCallback(
    (value: IGroupBy) => {
      // Extract unit from title if present (e.g., "Field Name (unit)")
      const unitMatch = value.title.match(/\(([^)]+)\)/);
      const unit = unitMatch && unitMatch.length > 1 ? unitMatch[1] : undefined;

      // Check if field is already selected
      if (selectedFields.some((f) => f.field === value.property)) {
        closeFieldSelector();
        return;
      }

      // Create new field object
      const newField: ScenarioField = {
        name: value.title,
        product: value.product,
        field: value.property,
        unit
      };

      // Add the field
      onAddField(newField);
      closeFieldSelector();
    },
    [selectedFields, onAddField, closeFieldSelector]
  );

  const handleRemoveField = useCallback(
    (fieldId: string) => {
      onRemoveField(fieldId);
    },
    [onRemoveField]
  );

  return {
    isFieldSelectorOpen,
    openFieldSelector,
    closeFieldSelector,
    handleFieldSelect,
    handleRemoveField
  };
};
